/**
 * ANIMATION_META — 애니메이션 프리셋 메타데이터 (remotion-free 순수 데이터).
 *
 * remotion 을 import 하지 않는다 — 그래야 서버 hydrate 라우트(Next.js API route)에서
 * editor_doc → 네이티브 변환 매퍼가 이 메타를 remotion 로드 없이 참조할 수 있다.
 * easing 은 함수가 아니라 AnimationEasingKey 키로 저장한다(properties[].easing·defaultEasing).
 * 렌더 시점(calculate-animation)이 키를 ANIMATION_EASINGS 함수로 해소한다.
 */
import type { AnimationType, AnimationMeta } from './animation-item-type';

// slide/wipe 애니메이션의 기본 이동 거리(%). 순수 상수(remotion-free)라 여기 둔다.
const DEFAULT_SLIDE_DISTANCE = 100;

export const ANIMATION_META = {
  fadeIn: {
    category: 'in',
    properties: [
      { property: 'opacity', inputRange: [0, 15], outputRange: [0, 1] },
    ],
    name: 'Fade In',
  },
  zoomIn: {
    category: 'in',
    properties: [
      { property: 'scale', inputRange: [0, 15], outputRange: [1, 1.5] },
    ],
    name: 'Zoom In',
  },
  fadeOut: {
    category: 'out',
    properties: [
      { property: 'opacity', inputRange: [0, 15], outputRange: [1, 0] },
    ],
    name: 'Fade Out',
  },
  zoomOut: {
    category: 'out',
    properties: [
      { property: 'scale', inputRange: [0, 15], outputRange: [1, 0.5] },
    ],
    name: 'Zoom Out',
  },
  // 왕복 애니 — 끝값까지 갔다가 시작 크기로 돌아온다. 왕복은 중간 keyframe 이 아니라 곡선
  // (roundTrip)이 만든다 → 값 카드에는 "시작·끝" 2칸만 뜨고 되돌아옴은 곡선이 책임진다.
  zoom1: {
    category: 'combo',
    properties: [
      {
        property: 'scale',
        inputRange: [0, 30],
        outputRange: [1, 0.5],
        easing: 'roundTrip',
      },
    ],
    name: 'Zoom 1',
  },
  zoom2: {
    category: 'combo',
    properties: [
      {
        property: 'scale',
        inputRange: [0, 30],
        outputRange: [1, 1.5],
        easing: 'roundTrip',
      },
    ],
    name: 'Zoom 2',
  },
  // Slide In 애니메이션
  slideInLeft: {
    category: 'in',
    properties: [
      {
        property: 'translateX',
        inputRange: [0, 15],
        outputRange: [-DEFAULT_SLIDE_DISTANCE, 0],
      },
    ],
    name: 'Slide In Left',
  },
  slideInRight: {
    category: 'in',
    properties: [
      {
        property: 'translateX',
        inputRange: [0, 15],
        outputRange: [DEFAULT_SLIDE_DISTANCE, 0],
      },
    ],
    name: 'Slide In Right',
  },
  slideInTop: {
    category: 'in',
    properties: [
      {
        property: 'translateY',
        inputRange: [0, 15],
        outputRange: [-DEFAULT_SLIDE_DISTANCE, 0],
      },
    ],
    name: 'Slide In Top',
  },
  slideInBottom: {
    category: 'in',
    properties: [
      {
        property: 'translateY',
        inputRange: [0, 15],
        outputRange: [DEFAULT_SLIDE_DISTANCE, 0],
      },
    ],
    name: 'Slide In Bottom',
  },
  // Slide Out 애니메이션
  slideOutLeft: {
    category: 'out',
    properties: [
      {
        property: 'translateX',
        inputRange: [0, 15],
        outputRange: [0, -DEFAULT_SLIDE_DISTANCE],
      },
    ],
    name: 'Slide Out Left',
  },
  slideOutRight: {
    category: 'out',
    properties: [
      {
        property: 'translateX',
        inputRange: [0, 15],
        outputRange: [0, DEFAULT_SLIDE_DISTANCE],
      },
    ],
    name: 'Slide Out Right',
  },
  slideOutTop: {
    category: 'out',
    properties: [
      {
        property: 'translateY',
        inputRange: [0, 15],
        outputRange: [0, -DEFAULT_SLIDE_DISTANCE],
      },
    ],
    name: 'Slide Out Top',
  },
  slideOutBottom: {
    category: 'out',
    properties: [
      {
        property: 'translateY',
        inputRange: [0, 15],
        outputRange: [0, DEFAULT_SLIDE_DISTANCE],
      },
    ],
    name: 'Slide Out Bottom',
  },
  // Wipe In 애니메이션 — 콘텐츠가 한 방향에서 마스크로 드러난다.
  wipeInLeft: {
    category: 'in',
    properties: [
      { property: 'clipRight', inputRange: [0, 15], outputRange: [100, 0] },
    ],
    name: 'Wipe In Left',
  },
  wipeInRight: {
    category: 'in',
    properties: [
      { property: 'clipLeft', inputRange: [0, 15], outputRange: [100, 0] },
    ],
    name: 'Wipe In Right',
  },
  wipeInTop: {
    category: 'in',
    properties: [
      { property: 'clipBottom', inputRange: [0, 15], outputRange: [100, 0] },
    ],
    name: 'Wipe In Top',
  },
  wipeInBottom: {
    category: 'in',
    properties: [
      { property: 'clipTop', inputRange: [0, 15], outputRange: [100, 0] },
    ],
    name: 'Wipe In Bottom',
  },
  // Wipe Out 애니메이션 — Wipe In 의 대칭(같은 변을 다시 가린다).
  wipeOutLeft: {
    category: 'out',
    properties: [
      { property: 'clipRight', inputRange: [0, 15], outputRange: [0, 100] },
    ],
    name: 'Wipe Out Left',
  },
  wipeOutRight: {
    category: 'out',
    properties: [
      { property: 'clipLeft', inputRange: [0, 15], outputRange: [0, 100] },
    ],
    name: 'Wipe Out Right',
  },
  wipeOutTop: {
    category: 'out',
    properties: [
      { property: 'clipBottom', inputRange: [0, 15], outputRange: [0, 100] },
    ],
    name: 'Wipe Out Top',
  },
  wipeOutBottom: {
    category: 'out',
    properties: [
      { property: 'clipTop', inputRange: [0, 15], outputRange: [0, 100] },
    ],
    name: 'Wipe Out Bottom',
  },
  // 살짝 작게 시작해 끝값을 넘겼다가 안착하는 등장. 튀는 정도는 값이 아니라 곡선(overshoot)이
  // 만든다 — Inspector 에서 곡선을 바꿔 팝을 없애거나(등속) 왕복으로 바꿀 수 있다.
  popIn: {
    category: 'in',
    properties: [
      {
        property: 'scale',
        inputRange: [0, 15],
        outputRange: [0.8, 1],
        easing: 'overshoot',
      },
      { property: 'opacity', inputRange: [0, 15], outputRange: [0, 1] },
    ],
    name: 'Pop In',
  },
  // (위치 이동은 애니 타입이 아니라 아이템의 positionKeyframes 필드가 담당한다 —
  //  position-keyframes.ts. 구 move 타입은 삭제됐고 저장 데이터는 hydrate 시 마이그레이션.)
  // 느린 확대 + 드리프트(켄 번스). 등속이 자연스러워 linear.
  kenBurns: {
    category: 'combo',
    properties: [
      { property: 'scale', inputRange: [0, 30], outputRange: [1, 1.08] },
      { property: 'translateX', inputRange: [0, 30], outputRange: [0, -2] },
      { property: 'translateY', inputRange: [0, 30], outputRange: [0, -1.5] },
    ],
    name: 'Ken Burns',
    defaultEasing: 'linear',
  },
  // 한 번 커졌다 돌아오는 강조. 되돌아옴은 roundTrip 곡선이 만든다(중간 keyframe 없음).
  // 구간은 start/endTimeInSeconds 로 지정.
  pulse: {
    category: 'combo',
    properties: [
      {
        property: 'scale',
        inputRange: [0, 30],
        outputRange: [1, 1.08],
        easing: 'roundTrip',
      },
    ],
    name: 'Pulse',
  },
  // 흐릿하게 시작해 초점이 맞으며 나타나는 등장. opacity 를 포함하므로 fadeIn 과
  // 동시 적용 불가(canAddAnimation opacity 충돌) — zoomIn/slideIn 계열과 조합한다.
  blurIn: {
    category: 'in',
    properties: [
      { property: 'blur', inputRange: [0, 15], outputRange: [7, 0] },
      { property: 'opacity', inputRange: [0, 15], outputRange: [0, 1] },
    ],
    name: 'Blur In',
  },
  // 좌우 흔들림 강조(충격 연출). 진동은 keyframe 이 아니라 shake 곡선이 만든다 —
  // outputRange 끝값 = 진폭 px. 구간은 combo 규칙(start/endTimeInSeconds)으로 지정.
  shakeX: {
    category: 'combo',
    properties: [
      {
        property: 'translateXPx',
        inputRange: [0, 30],
        outputRange: [0, 12],
        easing: 'shake',
      },
    ],
    name: 'Shake X',
  },
  // 살짝 기운 채 등장해 수평으로 풀리는 회전 등장. 각도·방향은 customOutputRanges 로 조절.
  rotateIn: {
    category: 'in',
    properties: [
      { property: 'rotation', inputRange: [0, 15], outputRange: [8, 0] },
    ],
    name: 'Rotate In',
  },
  // 전 구간 미세 회전 드리프트(카메라 tilt 느낌). 등속이 자연스러워 linear.
  rotateDrift: {
    category: 'combo',
    properties: [
      { property: 'rotation', inputRange: [0, 30], outputRange: [-1.2, 1.2] },
    ],
    name: 'Rotate Drift',
    defaultEasing: 'linear',
  },
  // 제자리 50px 아래에서 떠오르며 페이드 인 — 화면 끝 슬라이드가 아니라 캔버스 px 고정 이동.
  riseIn: {
    category: 'in',
    properties: [
      { property: 'opacity', inputRange: [0, 15], outputRange: [0, 1] },
      { property: 'translateYPx', inputRange: [0, 15], outputRange: [50, 0] },
    ],
    name: 'Rise In',
  },
} satisfies Record<AnimationType, AnimationMeta>;
